import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createParentColor,
  getAllParentColors,
  updateParentColor,
  deleteParentColor,
  clearErrors,
} from "../../redux/actions/parentColor";
import "./AdminParentColors.scss";

const AdminParentColors = () => {
  const dispatch = useDispatch();

  const {
    isLoading,
    error,
    colors = [],
    success,
    message,
  } = useSelector((state) => state.parentColor);

  // --- СТЕЙТ ДЛЯ СТВОРЕННЯ ---
  const [name, setName] = useState("");
  const [images, setImages] = useState([]); // base64 або URL

  // --- СТЕЙТ ДЛЯ РЕДАГУВАННЯ ---
  const [editMode, setEditMode] = useState(false);
  const [editColorId, setEditColorId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editImagesToDelete, setEditImagesToDelete] = useState([]); // public_id
  const [newImages, setNewImages] = useState([]); // base64

  // Запитати кольори один раз при монтуванні
  useEffect(() => {
    dispatch(getAllParentColors());
  }, [dispatch]);

  // Відстеження помилок, повідомлень, а також успішних операцій
  useEffect(() => {
    if (error) {
      alert(error);
      dispatch(clearErrors());
    }

    if (message) {
      alert(message);
    }

    // Якщо операція (створення, оновлення чи видалення) пройшла успішно,
    // заново отримуємо список кольорів, щоб показати оновлені дані
    if (success) {
      dispatch(getAllParentColors());
    }
  }, [error, message, success, dispatch]);

  // --- Обробник створення нового кольору ---
  const handleCreate = () => {
    if (!name.trim()) {
      return alert("Вкажіть назву кольору!");
    }
    dispatch(createParentColor(name, images));
    // Після відправки екшену можна очистити локальні поля,
    // але саме оновлення списку відбудеться після success
    setName("");
    setImages([]);
  };

  // --- Обробник видалення кольору ---
  const handleDelete = (id) => {
    if (window.confirm("Ви впевнені, що хочете видалити цей колір?")) {
      dispatch(deleteParentColor(id));
    }
  };

  // --- Завантаження файлів (base64) ---
  const handleFileChange = (e, isNewImages = false) => {
    const files = Array.from(e.target.files);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2) {
          if (isNewImages) {
            setNewImages((prev) => [...prev, reader.result]);
          } else {
            setImages((prev) => [...prev, reader.result]);
          }
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // --- Увімкнення режиму редагування ---
  const handleEdit = (colorObj) => {
    setEditMode(true);
    setEditColorId(colorObj._id);
    setEditName(colorObj.name);
    setEditImagesToDelete([]);
    setNewImages([]);
  };

  // Видалення конкретного нового зображення з newImages
  const removeNewImage = (idx) => {
    setNewImages((prev) => prev.filter((_, i) => i !== idx));
  };

  // Додавання до списку на видалення зображення, яке вже є в БД
  const handleMarkForDelete = (public_id) => {
    setEditImagesToDelete((prev) => [...prev, public_id]);
  };

  // --- Обробник оновлення кольору ---
  const handleUpdate = () => {
    if (!editColorId) return;

    const updatedData = {
      name: editName,
      imagesToDelete: editImagesToDelete,
      newImages, // base64
    };

    dispatch(updateParentColor(editColorId, updatedData));

    // Вимикаємо режим редагування, очищаємо поля
    setEditMode(false);
    setEditColorId(null);
    setEditName("");
    setNewImages([]);
    setEditImagesToDelete([]);
  };

  // Скасувати редагування
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditName("");
    setEditColorId(null);
    setNewImages([]);
    setEditImagesToDelete([]);
  };

  return (
    <div className="parent-color-manager">
      <h2>Батьківські Кольори</h2>

      {/* Блок створення нового кольору */}
      {!editMode && (
        <div className="create-section">
          <input
            type="text"
            className="apple-input"
            placeholder="Назва кольору..."
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <label className="apple-btn secondary">
            Обрати зображення
            <input
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, false)}
            />
          </label>

          {images.length > 0 && (
            <div style={{ margin: "1rem 0" }}>
              <p>Обрано: {images.length} зображення(-нь)</p>
            </div>
          )}

          <button
            className="apple-btn"
            onClick={handleCreate}
            disabled={isLoading}
          >
            Створити
          </button>
        </div>
      )}

      {/* Блок редагування */}
      {editMode && (
        <div className="edit-section">
          <input
            type="text"
            className="apple-input"
            placeholder="Нова назва кольору..."
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />

          {/* Додати нові зображення */}
          <label className="apple-btn secondary">
            Додати зображення
            <input
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, true)}
            />
          </label>

          {newImages.length > 0 && (
            <div style={{ margin: "1rem 0" }}>
              <p>Нові зображення:</p>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {newImages.map((img, idx) => (
                  <div key={idx} style={{ position: "relative" }}>
                    <img
                      src={img}
                      alt="new"
                      style={{ width: "50px", height: "50px", objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      onClick={() => removeNewImage(idx)}
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        background: "rgba(255,255,255,0.8)",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            className="apple-btn"
            onClick={handleUpdate}
            disabled={isLoading}
          >
            Зберегти зміни
          </button>

          <button className="apple-btn secondary" onClick={handleCancelEdit}>
            Скасувати
          </button>
        </div>
      )}

      {/* Список кольорів */}
      <div className="color-list">
        {colors.map((c) => (
          <div className="color-item" key={c._id}>
            <div className="color-info">
              <span className="color-name">{c.name}</span>
              <div className="color-images">
                {c.images?.map((imgObj, idx) => (
                  <div key={idx} style={{ position: "relative" }}>
                    <img src={imgObj.url} alt={c.name} />
                    {/* Кнопка для позначення зображення до видалення (лише в режимі редагування) */}
                    {editMode && editColorId === c._id && (
                      <button
                        type="button"
                        onClick={() => handleMarkForDelete(imgObj.public_id)}
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          background: "rgba(255,255,255,0.8)",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        X
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="actions">
              <button
                className="apple-btn secondary"
                onClick={() => handleEdit(c)}
              >
                Редагувати
              </button>
              <button
                className="apple-btn danger"
                onClick={() => handleDelete(c._id)}
              >
                Видалити
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminParentColors;
